package com.eladfinish.bible.contest.screens.names.ui

import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.TextUnit
import com.eladfinish.bible.contest.analytics.crashlytics.AppCrashlytics
import com.eladfinish.bible.contest.extensions.convertToDataClass
import com.eladfinish.bible.contest.extensions.toJson
import com.eladfinish.bible.contest.screens.names.data.InternalNameLink
import com.eladfinish.bible.contest.screens.names.data.LinkInfo
import com.eladfinish.bible.contest.screens.names.data.NamesLink
import com.eladfinish.bible.contest.screens.names.utils.LinkInfoProvider
import com.eladfinish.bible.contest.screens.names.utils.getInternalLinkInfo

// https://github.com/firefinchdev/linkify-text/blob/master/linkifytext/src/main/java/com/linkifytext/LinkifyText.kt
@Composable
fun LinkifyText(
    linkInfoProvider: LinkInfoProvider,
    letter: Char,
    name: String,
    text: String,
    modifier: Modifier = Modifier,
    linkColor: Color = Color.Blue,
    linkEntire: Boolean = false,
    color: Color = Color.Unspecified,
    fontSize: TextUnit = TextUnit.Unspecified,
    fontStyle: FontStyle? = null,
    fontWeight: FontWeight? = null,
    fontFamily: FontFamily? = null,
    letterSpacing: TextUnit = TextUnit.Unspecified,
    textDecoration: TextDecoration? = null,
    textAlign: TextAlign? = null,
    lineHeight: TextUnit = TextUnit.Unspecified,
    overflow: TextOverflow = TextOverflow.Clip,
    softWrap: Boolean = true,
    maxLines: Int = Int.MAX_VALUE,
    onTextLayout: (TextLayoutResult) -> Unit = {},
    style: TextStyle = LocalTextStyle.current,
    clickable: Boolean = true,
    onLinkClicked: ((linkText: String) -> Unit)? = null,
    onInternalLinkClicked: ((internalNameLink: InternalNameLink) -> Unit)? = null,
) {
    val uriHandler = LocalUriHandler.current
    val linkInfos =
        if (linkEntire) {
            listOf(LinkInfo(url = text, start = 0, end = text.length))
        } else {
            linkInfoProvider.getLinkInfos(
                text = text,
            )
        }

    val internalNameLinks = getInternalLinkInfo(
        letter = letter,
        name = name,
        text = text,
    )

    val links: List<NamesLink> = linkInfos + internalNameLinks

    val annotatedString = buildAnnotatedString {
        append(text)
        links.forEachIndexed { index, link ->
            addStyle(
                style = SpanStyle(
                    color = linkColor,
                    textDecoration = TextDecoration.Underline,
                ),
                start = link.start,
                end = link.end,
            )

            addStringAnnotation(
                tag = "${link::class.simpleName}_tag_$index",
                annotation = when (link) {
                    is InternalNameLink -> link.toJson<InternalNameLink>()
                    is LinkInfo -> link.url
                },
                start = link.start,
                end = link.end,
            )
        }
    }
    if (clickable) {
        MyClickableText(
            text = annotatedString,
            modifier = modifier,
            color = color,
            fontSize = fontSize,
            fontStyle = fontStyle,
            fontWeight = fontWeight,
            fontFamily = fontFamily,
            letterSpacing = letterSpacing,
            textDecoration = textDecoration,
            textAlign = textAlign,
            lineHeight = lineHeight,
            overflow = overflow,
            softWrap = softWrap,
            maxLines = maxLines,
            onTextLayout = onTextLayout,
            style = style,
            onClick = { offset ->
                annotatedString.getStringAnnotations(
                    start = offset,
                    end = offset,
                ).firstOrNull()?.let { result ->
                    if (linkEntire) {
                        onLinkClicked?.invoke(annotatedString.substring(result.start, result.end))
                    } else {
                        val tag = result.tag
                        when {
                            tag.startsWith(LinkInfo::class.simpleName.orEmpty()) -> {
                                try {
                                    uriHandler.openUri(result.item)
                                } catch (e: Exception) {
                                    AppCrashlytics.logException(e)
                                }
                                onLinkClicked?.invoke(annotatedString.substring(result.start, result.end))
                            }

                            tag.startsWith(InternalNameLink::class.simpleName.orEmpty()) -> {
                                onInternalLinkClicked?.invoke(result.item.convertToDataClass())
                            }
                        }
                    }
                }
            }
        )
    } else {
        Text(
            text = annotatedString,
            modifier = modifier,
            color = color,
            fontSize = fontSize,
            fontStyle = fontStyle,
            fontWeight = fontWeight,
            fontFamily = fontFamily,
            letterSpacing = letterSpacing,
            textDecoration = textDecoration,
            textAlign = textAlign,
            lineHeight = lineHeight,
            overflow = overflow,
            softWrap = softWrap,
            maxLines = maxLines,
            onTextLayout = onTextLayout,
            style = style,
        )
    }
}
